import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { SpreadsheetComponent } from '../components/SpreadsheetComponent';
import DeploymentOptions from '../components/DeploymentOptions';

export function DataDictionaryGenerator() {
  const [sourceName, setSourceName] = useState('');
  const [description, setDescription] = useState('');
  const [currentSource, setCurrentSource] = useState('');
  const [tableList, setTableList] = useState('');
  const [outputFormat, setOutputFormat] = useState('markdown');
  const [generatedDictionary, setGeneratedDictionary] = useState('');
  const [sampleData, setSampleData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const formatOptions = [
    { value: 'markdown', label: 'Markdown' },
    { value: 'table', label: 'Table View' },
    { value: 'json', label: 'JSON' },
    { value: 'yaml', label: 'YAML' }
  ];

  const quickStartGuides = [
    {
      title: "Data Dictionary Design",
      description: "Define your data sources and structure for comprehensive documentation.",
      keywords: ["Schema", "Metadata", "Documentation"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Sample Data Generation",
      description: "Generate realistic sample data based on your data dictionary.",
      keywords: ["Data", "Samples", "Generation"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    },
    {
      title: "Review & Export",
      description: "Review and export your data dictionary and sample data.",
      keywords: ["Export", "Review", "Format"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setDescription(transcribedText);
  };

  const generateContent = async () => {
    if (!sourceName.trim() || !description.trim()) {
      toast.error('Please provide source name and description');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Create a comprehensive data dictionary and sample data for:
Source Name: ${sourceName}
Description: ${description}
Current Source: ${currentSource}
Tables: ${tableList}

Please provide:
1. Data Dictionary:
- Table descriptions
- Field definitions (Field Name, Source Table, Source Feed, Description, Rules/Notes)
- Derived field logic
- Relationships and dependencies
- Notes section

2. Sample Data:
- Generate realistic sample data for each table
- Include appropriate data types
- Maintain referential integrity
- Add example records

Format the data dictionary in ${outputFormat} format with clear sections and include both the dictionary and sample data structure.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          // Parse the response to separate dictionary and sample data
          const [dictionary, sampleDataStr] = generatedText.split('---SAMPLE_DATA---');
          setGeneratedDictionary(dictionary.trim());
          
          // Parse sample data into spreadsheet format
          try {
            const sampleDataObj = JSON.parse(sampleDataStr);
            setSampleData(sampleDataObj);
          } catch (e) {
            console.error('Error parsing sample data:', e);
            setSampleData([]);
          }
          
          toast.success('Content generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.message || 'Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-500/40 via-purple-300/10 to-purple-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-400/40 via-purple-300/10 to-purple-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-purple-800/50 border border-purple-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></div>
                <span className="text-sm text-purple-100">AI-Powered Documentation</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Data Dictionary Generator
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-purple-200 max-w-xl">
                Generate comprehensive data dictionaries and sample data
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple Formats</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Sample Data</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          <div className="space-y-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* Source Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source Name
                </label>
                <input
                  type="text"
                  value={sourceName}
                  onChange={(e) => setSourceName(e.target.value)}
                  placeholder="Enter the source name..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Description with Voice Recording */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brief Description
                </label>
                <VoiceRecorder 
                  onTranscriptionComplete={handleTranscriptionComplete}
                  placeholder="Click to record description..."
                  className="mb-4"
                />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="4"
                  placeholder="Describe your data source..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Current Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Source
                </label>
                <input
                  type="text"
                  value={currentSource}
                  onChange={(e) => setCurrentSource(e.target.value)}
                  placeholder="Enter the current source..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Table List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table List
                </label>
                <textarea
                  value={tableList}
                  onChange={(e) => setTableList(e.target.value)}
                  rows="3"
                  placeholder="Enter table names (comma-separated)..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Output Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {formatOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setOutputFormat(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        outputFormat === option.value
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={generateContent}
                disabled={isGenerating}
                className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Content...
                  </div>
                ) : (
                  'Generate Content'
                )}
              </button>
            </div>

            {/* Generated Content Section */}
            {generatedDictionary && (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Data Dictionary</h3>
                  <button
                    onClick={() => copyToClipboard(generatedDictionary)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap">{generatedDictionary}</div>
                </div>
              </div>
            )}

            {/* Sample Data Spreadsheet */}
            {sampleData.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sample Data</h3>
                <SpreadsheetComponent
                  data={sampleData}
                  onDataChange={(newData) => setSampleData(newData)}
                />
              </div>
            )}

            {!generatedDictionary && !sampleData.length && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No content generated yet</h3>
                <p className="mt-1 text-sm text-gray-500">Fill in the details to generate your data dictionary and sample data.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="data-dictionary-generator" position="none" />
    </div>
  );
}

export default DataDictionaryGenerator; 